<script setup>
/**
 * A day of bookings, running past under the hero: two rows of slots drifting
 * in opposite directions, the way a studio's board or a salon's diary fills.
 *
 * Decoration — the whole band is hidden from screen readers; the sections
 * around it say what the product does. Each row is duplicated so the -50%
 * translate in motion.css loops without a seam.
 */
import { ref } from "vue";
import { useLanding } from "../../composables/useLanding";
import { usePauseOffscreen } from "../../composables/usePauseOffscreen";

const { rows, fullLabel, statusLabels } = useLanding().ticker;

const band = ref(null);
const { paused } = usePauseOffscreen(band);

function isFull(slot) {
  return slot.of && slot.taken === slot.of;
}
</script>

<template>
  <section
    ref="band"
    class="ticker"
    :class="{ 'is-paused': paused }"
    aria-hidden="true"
  >
    <div
      v-for="(row, r) in rows"
      :key="r"
      class="ticker__row marquee"
      :class="`ticker__row--${r}`"
    >
      <div class="marquee__track">
        <div
          v-for="(slot, i) in [...row, ...row]"
          :key="i"
          class="chip"
          :class="{ 'is-full': isFull(slot), 'is-open': slot.status === 'open' }"
        >
          <span class="chip__time">{{ slot.time }}</span>
          <span class="chip__name">{{ slot.name }}</span>

          <template v-if="slot.of">
            <span class="chip__count">
              {{ isFull(slot) ? fullLabel : `${slot.taken}/${slot.of}` }}
            </span>
            <span class="chip__track">
              <span class="chip__fill" :style="{ '--fill': slot.taken / slot.of }" />
            </span>
          </template>

          <span v-else class="chip__count">{{ statusLabels[slot.status] }}</span>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.ticker {
  display: grid;
  gap: var(--space-3);
  padding-block: var(--space-6);
  overflow: hidden;
  background: var(--brand);
}

.ticker__row .marquee__track {
  gap: var(--space-3);
  padding-inline-end: var(--space-3);
  animation-duration: 60s;
}

/* The second row runs the other way, a little slower. */
.ticker__row--1 .marquee__track {
  animation-direction: reverse;
  animation-duration: 75s;
}

/* ------------------------------------------------------------------ chip */

.chip {
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: var(--space-1) var(--space-3);
  min-inline-size: 220px;
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-lg);
  background: var(--surface);
  color: var(--ink);
  font-size: var(--text-sm);
  white-space: nowrap;
}

/* The second row reads as the evening: the same slots, on the dark side. */
.ticker__row--1 .chip {
  background: var(--brand-press);
  color: var(--on-brand);
}

.chip__time {
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
}

.chip__name {
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip__count {
  justify-self: end;
  padding: 1px var(--space-2);
  border-radius: var(--radius-pill);
  background: var(--brand-soft);
  color: var(--brand-ink);
  font-size: var(--text-xs);
  font-weight: var(--weight-bold);
  font-variant-numeric: tabular-nums;
}

.ticker__row--1 .chip__count {
  background: rgba(255, 255, 255, 0.16);
  color: var(--on-brand);
}

.chip.is-full .chip__count {
  background: var(--accent);
  color: var(--on-brand);
}

/* An open chair is the one thing on the board still waiting for someone. */
.chip.is-open {
  outline: 2px dashed rgba(255, 255, 255, 0.7);
  outline-offset: -2px;
  background: transparent;
  color: var(--on-brand);
}

.chip.is-open .chip__count {
  background: var(--surface);
  color: var(--brand-ink);
}

.chip__track {
  grid-column: 1 / -1;
  block-size: 4px;
  border-radius: var(--radius-pill);
  background: var(--brand-soft);
  overflow: hidden;
}

.ticker__row--1 .chip__track {
  background: rgba(255, 255, 255, 0.16);
}

.chip__fill {
  display: block;
  block-size: 100%;
  border-radius: inherit;
  background: var(--brand);
  transform-origin: right;
  transform: scaleX(var(--fill));
}

.ticker__row--1 .chip__fill {
  background: var(--surface);
}

.chip.is-full .chip__fill {
  background: var(--accent);
}

@media (max-width: 639px) {
  .ticker {
    padding-block: var(--space-5);
    gap: var(--space-2);
  }

  .chip {
    min-inline-size: 180px;
    padding: var(--space-2) var(--space-3);
  }
}
</style>
